import merge from 'lodash/merge';

import { createDashHttpClient } from '@/apis/dash.http';
import { env } from '@/env';

export const http = createDashHttpClient(env.instagramApiUrl);

export const httpCamelCase = createDashHttpClient(env.instagramApiUrl, {
  camelizeKeys: true,
});
// instance for utm data, some keys and value comes from user input
export const unformattedHttp = createDashHttpClient(env.instagramApiUrl, {
  camelizeKeys: false,
  decamelizeRequests: false,
});

export async function updatePost({ brandId, postSourceId }, config = {}) {
  return http.patch(`/brands/${brandId}/instagram/posts/${postSourceId}`, {}, config);
}

export async function getInstagramAccount({ brandId }, config = {}) {
  return httpCamelCase.get(`/accounts/${brandId}`, config);
}

export async function sendEmailReport({ brandId, reportType, ...params }, config = {}) {
  return http.get(
    `/brands/${brandId}/instagram/reports/email/${reportType}`,
    merge({ params }, config),
  );
}

export async function addAccount({ handle }, config = {}) {
  return http.post(`/instagram/accounts/${handle}`, {}, config);
}

export async function getPublicAccountsByIds({ instagramIds }, config = {}) {
  return httpCamelCase.post('/instagram/accounts/public', { instagramIds }, config);
}

export async function getAccountPosts(
  { instagramId, startDate, endDate, limit, sortOrder = 'asc' },
  config = {},
) {
  return httpCamelCase.get(
    `/instagram/accounts/${instagramId}/posts`,
    merge(
      {
        params: {
          startDate,
          endDate,
          limit,
          sortOrder,
        },
      },
      config,
    ),
  );
}

export async function getReport360Posts({ month, brandId }, config = {}) {
  return http.get(
    `/brands/${brandId}/instagram/reports/360/posts`,
    merge(
      {
        params: {
          month,
        },
      },
      config,
    ),
  );
}

export async function getReport360Stories({ month, brandId }, config = {}) {
  return http.get(
    `/brands/${brandId}/instagram/reports/360/stories`,
    merge(
      {
        params: {
          month,
        },
      },
      config,
    ),
  );
}

export async function getUgcAccounts({ brandId, startDate, endDate, limit, url }, config = {}) {
  return http.get(
    url || `/brands/${brandId}/ugc_accounts`,
    merge(
      {
        params: {
          startDate,
          endDate,
          limit,
        },
      },
      config,
    ),
  );
}

export async function getUgcAccountsCSV(
  { brandId, startDate, endDate, socketId, type },
  config = {},
) {
  return http.post(
    `/brands/${brandId}/ugc_accounts/csv`,
    {},
    merge(
      {
        params: {
          startDate,
          endDate,
          socketId,
          type,
        },
      },
      config,
    ),
  );
}

export async function getFollowersLost({ brandId, startDate, endDate, scale }, config = {}) {
  return http.get(
    `/brands/${brandId}/followers_lost_insights`,
    merge(
      {
        params: {
          startDate,
          endDate,
          scale,
        },
      },
      config,
    ),
  );
}

export async function getFollowersLostCSV(
  { brandId, startDate, endDate, scale, socketId },
  config = {},
) {
  return http.post(
    `/brands/${brandId}/followers_lost_insights/csv`,
    {},
    merge(
      {
        params: {
          startDate,
          endDate,
          scale,
          socketId,
        },
      },
      config,
    ),
  );
}

export async function getFollowerInsights({ brandId, startDate, endDate, scale }, config = {}) {
  return http.get(
    `/brands/${brandId}/followers_insights`,
    merge(
      {
        params: {
          startDate,
          endDate,
          scale,
        },
        camelizeKeys: true,
      },
      config,
    ),
  );
}

export async function getFollowerInsightsCSV(
  { brandId, startDate, endDate, scale, socketId, metrics },
  config = {},
) {
  return http.post(
    `/brands/${brandId}/followers_insights/csv`,
    {},
    merge(
      {
        params: {
          startDate,
          endDate,
          scale,
          socketId,
          metrics: metrics?.join(','),
        },
      },
      config,
    ),
  );
}

export async function getOnlineFollowers({ brandId, startDate, endDate }, config = {}) {
  return http.get(
    `/brands/${brandId}/online_followers`,
    merge({ params: { startDate, endDate } }, config),
  );
}

export async function getOnlineFollowersCSV(
  { brandId, startDate, endDate, socketId },
  config = {},
) {
  return http.post(
    `/brands/${brandId}/online_followers/csv`,
    { startDate, endDate, socketId },
    config,
  );
}

export async function getDemographicsInsights({ brandId, date, metric }, config = {}) {
  return http.get(
    `/brands/${brandId}/demographics_insights`,
    merge(
      {
        params: { date, metric },
      },
      config,
    ),
  );
}

export async function postDemographicsInsightsCSV(
  { brandId, date, metric, socketId, breakdown },
  config = {},
) {
  return http.post(
    `/brands/${brandId}/demographics_insights/csv`,
    { date, metric, socketId, breakdown },
    config,
  );
}

export async function getInstagramUsers(
  { brandId, handle, after, limit, cache, mediaSince, mediaUntil },
  config = {},
) {
  return httpCamelCase.get(
    `/brands/${brandId}/instagram_users`,
    merge({ params: { handle, after, limit, cache, mediaSince, mediaUntil } }, config),
  );
}

export async function getBrandInstagramAccount({ brandId }, config = {}) {
  return httpCamelCase.get(`/brands/${brandId}/instagram/account`, config);
}

export async function getInstagramRightsRequestMediaCounts(
  { brandId, startDate, endDate, userId, contentRightsType },
  config = {},
) {
  return http.get(
    `/brands/${brandId}/instagram/rights_request_media/counts`,
    merge(
      {
        params: {
          startDate,
          endDate,
          userId,
          contentRightsType,
        },
      },
      config,
    ),
  );
}

export async function getInstagramRightsRequestMediaCsv(
  { brandId, startDate, endDate, userId, contentRightsType, socketId, type },
  config = {},
) {
  return http.get(
    `/brands/${brandId}/instagram/rights_request_media/csv`,
    merge(
      {
        params: {
          startDate,
          endDate,
          userId,
          contentRightsType,
          socketId,
          type,
        },
      },
      config,
    ),
  );
}

export async function addRightsRequestFacebookComment({ brandId, postId, message }, config = {}) {
  return http.post(
    `/brands/${brandId}/instagram/rights_request_media/${postId}/facebook_comment`,
    { message },
    config,
  );
}

export async function getRightsRequestComments({ brandId }, config = {}) {
  return httpCamelCase.get(`/brands/${brandId}/instagram/rights_request_comments`, config);
}

export async function addRightsRequestComment(
  { brandId, comment, name, requestDocumentId, requestHashtagId, rightsDurationDays },
  config = {},
) {
  return httpCamelCase.post(
    `/brands/${brandId}/instagram/rights_request_comments`,
    {
      comment,
      name,
      requestDocumentId,
      requestHashtagId,
      rightsDurationDays,
    },
    config,
  );
}

export async function updateRightsRequestComment(
  { brandId, commentId, comment, name, requestDocumentId, requestHashtagId, rightsDurationDays },
  config = {},
) {
  return httpCamelCase.patch(
    `/brands/${brandId}/instagram/rights_request_comments/${commentId}`,
    { comment, name, requestDocumentId, requestHashtagId, rightsDurationDays },
    config,
  );
}

export async function deleteRightsRequestComment({ brandId, commentId }, config = {}) {
  return httpCamelCase.delete(
    `/brands/${brandId}/instagram/rights_request_comments/${commentId}`,
    config,
  );
}

export async function getInstagramUgcComments({ brandId, mediaId }, config = {}) {
  return httpCamelCase.get(`/brands/${brandId}/instagram/ugc_comments/${mediaId}`, config);
}

export async function getIdealPostTimes({ brandId, timezone }, config = {}) {
  return http.get(
    `/brands/${brandId}/instagram/ideal_post_times`,
    merge(
      {
        params: {
          timezone,
        },
      },
      config,
    ),
  );
}

export async function getRightsRequestMedia(
  { brandId, startDate, endDate, userId, contentRightsType, limit, offset },
  config = {},
) {
  return http.get(
    `/brands/${brandId}/instagram/rights_request_media`,
    merge(
      {
        params: {
          startDate,
          endDate,
          userId,
          contentRightsType,
          limit,
          offset,
        },
      },
      config,
    ),
  );
}

export async function getPosts({ brandId }, config = {}) {
  return http.get(`/brands/${brandId}/instagram/posts`, config);
}

export async function fetchPostBySourceId({ brandId, sourceId }, config = {}) {
  return http.post(
    `/brands/${brandId}/instagram/posts`,
    { ids: [sourceId] },
    merge(
      {
        params: {
          excludeFields: '[]',
        },
      },
      config,
    ),
  );
}

export async function addRelationship({ brandId, instagramName }, config = {}) {
  return http.post(`/brands/${brandId}/instagram/relationships`, { instagramName }, config);
}

export async function getReachStats({ brandId, startDate, endDate, scale }, config = {}) {
  return http.get(
    `/brands/${brandId}/reach/stats`,
    merge(
      {
        params: {
          startDate,
          endDate,
          scale,
        },
      },
      config,
    ),
  );
}

export async function getRelationshipList(
  {
    brandId,
    url,
    keyword,
    sort,
    publishedStartDate,
    publishedEndDate,
    isBusiness,
    type,
    onlyPostsWithType,
    tagIds,
    tagFilterType,
    isFbConnected,
    acceptanceStatuses,
  },
  config = {},
) {
  return httpCamelCase.get(
    url || `/brands/${brandId}/instagram/relationships`,
    merge(
      {
        params: {
          keyword,
          sort,
          publishedStartDate,
          publishedEndDate,
          isBusiness,
          type,
          onlyPostsWithType,
          tagIds,
          tagFilterType,
          isFbConnected,
          acceptanceStatuses,
        },
      },
      config,
    ),
  );
}

export async function exportRelationshipOverviewCSV(
  {
    brandId,
    keyword,
    sort,
    publishedStartDate,
    publishedEndDate,
    isBusiness,
    type,
    onlyPostsWithType,
    tagIds,
    tagFilterType,
    isFbConnected,
    acceptanceStatuses,
    csvType,
    socketId,
  },
  config = {},
) {
  return httpCamelCase.post(
    `/brands/${brandId}/instagram/relationships/csv`,
    {},
    merge(
      {
        params: {
          keyword,
          sort,
          publishedStartDate,
          publishedEndDate,
          isBusiness,
          type,
          onlyPostsWithType,
          tagIds,
          tagFilterType,
          isFbConnected,
          acceptanceStatuses,
          csvType,
          socketId,
        },
      },
      config,
    ),
  );
}

export async function findRelationshipByHandle({ brandId, handle }, config = {}) {
  return httpCamelCase.get(
    `/brands/${brandId}/instagram/relationships`,
    merge(
      {
        params: {
          handle,
        },
      },
      config,
    ),
  );
}

export async function findMediaRelationshipByHandle({ brandId, handle }, config = {}) {
  return httpCamelCase.get(
    `/brands/${brandId}/instagram/relationships`,
    merge(
      {
        params: {
          handle,
        },
      },
      config,
    ),
  );
}

export async function findRelationshipByInfluencerId(
  { brandId, influencerId, connectInstagramInfluencerToken },
  config = {},
) {
  return httpCamelCase.get(
    `/brands/${brandId}/instagram/relationships`,
    merge(
      {
        params: {
          influencerId,
          connectInstagramInfluencerToken,
        },
      },
      config,
    ),
  );
}

export async function getRightsRequestMediaPost({ brandId, id }, config = {}) {
  return http.get(`/brands/${brandId}/instagram/rights_request_media/${id}`, config);
}

export async function createRightsRequestMediaPost(
  { brandId, postId, type, requestComment, termLink, trackingHashtag, rightsDurationDays },
  config = {},
) {
  return http.post(
    `/brands/${brandId}/instagram/rights_request_media/${postId}`,
    {
      type,
      requestComment,
      termLink,
      trackingHashtag,
      ...(rightsDurationDays != null && { rightsDurationDays }),
    },
    config,
  );
}

export async function bulkCreateRightsRequestMediaPost(
  { brandId, mediaIds, type, requestComment, termLink, trackingHashtag, rightsDurationDays },
  config = {},
) {
  return http.post(
    `/brands/${brandId}/instagram/rights_request_media`,
    { mediaIds, type, requestComment, termLink, trackingHashtag, rightsDurationDays },
    config,
  );
}

export async function getRelationship(
  { brandId, relationshipId, type, onlyVideoPosts, publishedStartDate, publishedEndDate },
  config = {},
) {
  return httpCamelCase.get(
    `/brands/${brandId}/instagram/relationships/${relationshipId}`,
    merge(
      {
        params: {
          type,
          onlyVideoPosts,
          publishedStartDate,
          publishedEndDate,
        },
      },
      config,
    ),
  );
}

export async function createRelationship({ brandId, handle }, config = {}) {
  return httpCamelCase.post(`/brands/${brandId}/instagram/relationships`, { handle }, config);
}

export async function updateRelationship(
  {
    brandId,
    relationshipId,
    email,
    invitationStatus,
    emailContent,
    influencerId,
    connectInstagramInfluencerToken,
    notes,
    hasPiq,
    newPath,
  },
  config = {},
) {
  if (influencerId && connectInstagramInfluencerToken) {
    return httpCamelCase.patch(
      `/brands/${brandId}/instagram/relationships/${relationshipId}`,
      { email, invitationStatus, emailContent, notes, hasPiq, newPath },
      merge(
        {
          params: {
            influencerId,
            connectInstagramInfluencerToken,
          },
        },
        config,
      ),
    );
  }
  return httpCamelCase.patch(
    `/brands/${brandId}/instagram/relationships/${relationshipId}`,
    { email, invitationStatus, emailContent, notes, hasPiq, newPath },
    config,
  );
}

export async function updateRightsRequestMediaPost(
  { brandId, id, type, rightsDurationDays },
  config = {},
) {
  return http.patch(
    `/brands/${brandId}/instagram/rights_request_media/${id}`,
    { type, ...(rightsDurationDays != null && { rightsDurationDays }) },
    config,
  );
}

export async function deleteRightsRequestMediaPost({ brandId, id }, config = {}) {
  return http.delete(`/brands/${brandId}/instagram/rights_request_media/${id}`, config);
}

export async function getRightsRequestDocuments({ brandId }, config = {}) {
  return http.get(`/brands/${brandId}/instagram/rights_request_documents`, config);
}

export async function createRightsRequestDocuments({ brandId, url, displayName }, config = {}) {
  return http.post(
    `/brands/${brandId}/instagram/rights_request_documents`,
    { url, displayName },
    config,
  );
}

export async function updateRightsRequestDocuments({ brandId, id, url, displayName }, config = {}) {
  return http.patch(
    `/brands/${brandId}/instagram/rights_request_documents/${id}`,
    { url, displayName },
    config,
  );
}

export async function deleteRightsRequestDocuments({ brandId, id }, config = {}) {
  return http.delete(`/brands/${brandId}/instagram/rights_request_documents/${id}`, config);
}

export async function createRightsRequestDocumentsS3Url(
  { brandId, fileName, contentType },
  config = {},
) {
  return http.post(
    `/brands/${brandId}/instagram/rights_request_documents/s3_url`,
    { fileName, contentType },
    config,
  );
}

export async function getRightsRequestHashtags({ brandId }, config = {}) {
  return http.get(`/brands/${brandId}/instagram/rights_request_hashtags`, config);
}

export async function updateRightsRequestHashtags({ brandId, id, trackingHashtag }, config = {}) {
  return http.patch(
    `/brands/${brandId}/instagram/rights_request_hashtags/${id}`,
    {},
    merge(
      {
        params: {
          trackingHashtag,
        },
      },
      config,
    ),
  );
}

export async function deleteRelationship({ brandId, relationshipId }, config = {}) {
  return httpCamelCase.delete(
    `/brands/${brandId}/instagram/relationships/${relationshipId}`,
    config,
  );
}

export async function getRelationshipPosts(
  {
    brandId,
    type,
    relationshipId,
    onlyVideoPosts,
    publishedStartDate,
    publishedEndDate,
    sort,
    sourceInstagramId,
    isStories,
    url,
  },
  config = {},
) {
  return httpCamelCase.get(
    url || `/brands/${brandId}/instagram/relationships/${relationshipId}/relationship_posts`,
    merge(
      {
        params: {
          type,
          onlyVideoPosts,
          publishedStartDate,
          publishedEndDate,
          sourceInstagramId,
          isStories,
          sort,
        },
      },
      config,
    ),
  );
}

export async function getRelationshipRIQStoryPosts(
  {
    brandId,
    relationshipId,
    isStories,
    publishedStartDate,
    publishedEndDate,
    sourceInstagramId,
    url,
    sort,
  },
  config = {},
) {
  return httpCamelCase.get(
    url || `/brands/${brandId}/instagram/mentioned_story_posts`,
    merge(
      {
        params: {
          type: 'RIQ',
          relationshipId,
          isStories,
          publishedStartDate,
          publishedEndDate,
          sourceInstagramId,
          sort,
        },
      },
      config,
    ),
  );
}

export async function exportRelationshipRIQStoryPostsCSV(
  {
    brandId,
    relationshipId,
    type,
    socketId,
    sort,
    sourceInstagramId,
    publishedStartDate,
    publishedEndDate,
  },
  config = {},
) {
  return httpCamelCase.post(
    `/brands/${brandId}/instagram/mentioned_story_posts/csv`,
    {},
    merge(
      {
        params: {
          brandId,
          relationshipId,
          type,
          socketId,
          sort,
          sourceInstagramId,
          publishedStartDate,
          publishedEndDate,
        },
      },
      config,
    ),
  );
}

export async function getInstagramHashtags({ brandId }, config = {}) {
  return http.get(`/brands/${brandId}/instagram/hashtags`, merge({ camelizeKeys: true }, config));
}

export async function saveInstagramHashtag({ brandId, name }, config = {}) {
  return http.post(`/brands/${brandId}/instagram/hashtags`, { name }, config);
}

export async function deleteInstagramHashtag({ brandId, hashtagId }, config = {}) {
  return http.delete(`/brands/${brandId}/instagram/hashtags/${hashtagId}`, config);
}

export async function getUgcPostsCounts(
  { brandId, ugcMediaType, startDate, endDate, startHour },
  config = {},
) {
  return http.get(
    `/brands/${brandId}/instagram/ugc_posts/counts`,
    merge(
      {
        params: {
          ugcMediaType,
          startDate,
          endDate,
          startHour,
        },
      },
      config,
    ),
  );
}

export async function getUgcPostsCountsCsv(
  { brandId, startDate, endDate, ugcMediaType, socketId, type },
  config = {},
) {
  return http.get(
    `/brands/${brandId}/instagram/ugc_posts/posts_csv`,
    merge(
      {
        params: {
          startDate,
          endDate,
          ugcMediaType,
          socketId,
          type,
        },
      },
      config,
    ),
  );
}

export async function exportRelationshipPostsCSV(
  { brandId, relationshipId, type, socketId, sort, publishedStartDate, publishedEndDate },
  config = {},
) {
  return httpCamelCase.post(
    `/brands/${brandId}/instagram/relationships/${relationshipId}/relationship_posts/csv`,
    {},
    merge(
      {
        params: {
          type,
          socketId,
          sort,
          publishedStartDate,
          publishedEndDate,
        },
      },
      config,
    ),
  );
}

export async function addRelationshipTag({ brandId, relationshipId, tagId, tagName }, config = {}) {
  return httpCamelCase.post(
    `/brands/${brandId}/instagram/relationships/tags`,
    {},
    merge(
      {
        params: {
          relationshipId,
          tagId,
          tagName,
        },
      },
      config,
    ),
  );
}

export async function getBrandRelationshipTags({ brandId }, config = {}) {
  return httpCamelCase.get(`/brands/${brandId}/instagram/relationships/tags`, config);
}

export async function deleteRelationshipTag({ brandId, relationshipId, tagId }, config = {}) {
  return httpCamelCase.delete(
    `/brands/${brandId}/instagram/relationships/tags/${tagId}`,
    merge(
      {
        params: {
          relationshipId,
        },
      },
      config,
    ),
  );
}

export async function createOrUpdateInfluencer({ brandId, influencerInstagramId }, config = {}) {
  return httpCamelCase.post(
    `/brands/${brandId}/instagram/relationships/influencers`,
    {},
    merge(
      {
        params: { influencerInstagramId },
      },
      config,
    ),
  );
}

export async function getRecentlySearchedHashtags({ brandId }, config = {}) {
  return http.get(`/brands/${brandId}/instagram/hashtags/recently_searched_hashtags`, config);
}

export async function addMentionUGC({ brandId, facebookMediaId }, config = {}) {
  return httpCamelCase.post(
    `/instagram/relationships/add_mention_ugc`,
    { brandId, facebookMediaId },
    config,
  );
}

export async function getInstagramCustomUtmSettings({ brandId }, config = {}) {
  return unformattedHttp.get(`brands/${brandId}/custom_url_params`, config);
}

export async function saveInstagramCustomUtmSettings({ brandId, data }, config = {}) {
  return unformattedHttp.patch(`brands/${brandId}/custom_url_params`, data, config);
}

export function getInstagramAccountAllBrands({ brandIds }, config = {}) {
  return httpCamelCase.put(`/accounts`, { brand_ids: brandIds }, config);
}

export async function getInteractionInsights(
  { brandId, scale, startDate, endDate, metrics },
  config = {},
) {
  return httpCamelCase.get(
    `/brands/${brandId}/interaction_insights`,
    merge(
      {
        params: {
          scale,
          startDate,
          endDate,
          metrics: metrics.join(','),
        },
      },
      config,
    ),
  );
}

export async function postInteractionInsightsCSV(
  { brandId, scale, startDate, endDate, metrics, csvTitle, csvType, socketId },
  config = {},
) {
  return httpCamelCase.post(
    `/brands/${brandId}/interaction_insights/csv`,
    { scale, startDate, endDate, csvTitle, csvType, socketId, metrics: metrics.join(',') },
    config,
  );
}

export default {
  posts: {
    update: updatePost,
  },
};
